if (!customElements.get("reviews-carousel")) {
  class ReviewsCarousel extends HTMLElement {
    constructor() {
      super();
      this.previewTimers = [];
      this.isAutoplaying = false;
      this.hasStarted = false;
      this.userInteracted = false;
      this.autoplayEnabled = false;
      this.intersectionObserver = null;
      this.embla = null;
      this.isPageVisible = !document.hidden;
    }

    connectedCallback() {
      const carouselContainer = this.querySelector(".slides-container");
      const align = this.getAttribute("data-align") || "start";
      const loop = this.getAttribute("data-loop") === "true";
      const dragFree = this.getAttribute("data-drag-free") === "true";
      const scrollCount = this.getAttribute("data-slides-to-scroll") || 1;
      const controls = this.getAttribute("data-controls") === "true";
      const autoplay = this.getAttribute("data-autoplay") === "true";

      this.autoplayEnabled = autoplay;
      // How long non-video slides stay in focus
      this.slideDwell = parseInt(this.getAttribute("data-autoplay-interval")) || 3000;

      this.embla = EmblaCarousel(carouselContainer, {
        align,
        loop,
        dragFree,
        skipSnaps: true,
        slidesToScroll: parseInt(scrollCount),
      });

      if (controls) this.setupControls();
      this.setupDots();
      if (autoplay) {
        this.setupIntersectionObserver();
        this.setupAutoplay();
        this.setupVisibilityChange();
      }
    }

    // Number of whole slides that fit in the viewport, measured from the layout
    getSlidesPerView() {
      const slides = this.embla.slideNodes();
      const slideStep = slides.length > 1
        ? slides[1].offsetLeft - slides[0].offsetLeft
        : slides[0]?.offsetWidth;

      if (!slideStep) return 1;
      return Math.max(1, Math.floor(this.embla.containerNode().offsetWidth / slideStep));
    }

    setupVisibilityChange() {
      this.visibilityChangeHandler = () => {
        if (document.hidden) {
          this.isPageVisible = false;
          if (this.isAutoplaying) {
            this.pauseAutoplay();
          }
        } else {
          this.isPageVisible = true;
          if (this.hasStarted && !this.userInteracted) {
            this.resumeAutoplay();
          }
        }
      };

      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    pauseAutoplay() {
      this.clearAllTimers();
    
      const videos = this.querySelectorAll('.custom-video');
      videos.forEach(video => {
        if (!video.paused) {
          video.pause();
        }
      });
    
      const slides = this.querySelectorAll('.slide');
      slides.forEach(slide => {
        slide.classList.remove('active-preview');
      });
    }

    resumeAutoplay() {
      if (!this.isAutoplaying) return;
    
      const videos = this.querySelectorAll('.custom-video');
      videos.forEach(video => {
        video.currentTime = 0;
      });
    
      this.previewCurrentBatch();
    }

    setupIntersectionObserver() {
      const options = { 
        root: null, 
        rootMargin: '100px',
        threshold: 0.5 
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasStarted) {
            this.hasStarted = true;
            this.preloadBatchMetadata(0);
            this.startAutoplay();
          } else if (!entry.isIntersecting && this.hasStarted) {
            this.stopAutoplay();
            this.hasStarted = false;
          }
        });
      }, options);

      this.intersectionObserver.observe(this);
    }

    preloadBatchMetadata(startIndex) {
      const slides = this.embla.slideNodes();
      const endIndex = Math.min(startIndex + this.getSlidesPerView(), slides.length);

      for (let i = startIndex; i < endIndex; i++) {
        const video = slides[i]?.querySelector('.custom-video');
        if (video && video.readyState < 1) {
          video.preload = 'metadata';
          video.load();
        }
      }
    }

    preloadBatchAuto(startIndex) {
      const slides = this.embla.slideNodes();
      const endIndex = Math.min(startIndex + this.getSlidesPerView(), slides.length);

      for (let i = startIndex; i < endIndex; i++) {
        const video = slides[i]?.querySelector('.custom-video');
        if (video && video.readyState < 3) {
          video.preload = 'auto';
          video.load();
        }
      }
    }

    setupAutoplay() {
      this.embla.on('init', () => {
        if (this.hasStarted) {
          setTimeout(() => this.startAutoplaySequence(), 100);
        }
      });

      this.embla.on('pointerDown', () => {
        this.userInteracted = true;
        this.stopAutoplay();
      });

      let resumeTimer;
      this.embla.on('settle', () => {
        if (this.userInteracted) {
          clearTimeout(resumeTimer);
          resumeTimer = setTimeout(() => {
            this.userInteracted = false;
            const currentIndex = this.embla.selectedScrollSnap();
            this.preloadBatchMetadata(currentIndex);
            this.startAutoplay();
          }, 3000);
        }
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.isAutoplaying) return;
      this.isAutoplaying = true;
      this.startAutoplaySequence();
    }

    startAutoplaySequence() {
      if (!this.isAutoplaying) return;
      this.previewCurrentBatch();
    }

    stopAutoplay() {
      if (!this.isAutoplaying) return;
      this.isAutoplaying = false;
      this.clearAllTimers();
      this.pauseAllVideos();
    }

    async previewCurrentBatch() {
      if (!this.isAutoplaying || this.userInteracted || !this.isPageVisible) return;

      const slides = this.embla.slideNodes();
      const currentIndex = this.embla.selectedScrollSnap();
      const totalSlides = slides.length;
      const slidesPerView = this.getSlidesPerView();
    
      const remainingSlides = totalSlides - currentIndex;
      const slidesToPreview = Math.min(slidesPerView, remainingSlides);

      this.preloadBatchAuto(currentIndex);

      const nextBatchIndex = currentIndex + slidesPerView;
      if (nextBatchIndex < totalSlides) {
        this.preloadBatchMetadata(nextBatchIndex);
      }

      for (let i = 0; i < slidesToPreview; i++) {
        if (!this.isAutoplaying || this.userInteracted || !this.isPageVisible) break;
      
        const slideIndex = currentIndex + i;
        if (slideIndex < totalSlides) {
          await this.previewSlide(slides[slideIndex]);
        }
      }

      if (this.isAutoplaying && !this.userInteracted && this.isPageVisible) {
        const nextIndex = currentIndex + slidesPerView;
        const lastPossibleIndex = Math.max(0, totalSlides - slidesPerView);
      
        if (nextIndex < totalSlides && currentIndex < lastPossibleIndex) {
          const targetIndex = nextIndex > lastPossibleIndex ? lastPossibleIndex : nextIndex;
        
          this.preloadBatchAuto(targetIndex);
        
          this.embla.scrollTo(targetIndex);
          await this.wait(600);
          this.previewCurrentBatch();
        } 
        else if (this.getAttribute("data-loop") === "true") {
          this.preloadBatchAuto(0);
          this.embla.scrollTo(0);
          await this.wait(600);
          this.previewCurrentBatch();
        } 
        else {
          this.stopAutoplay();
        }
      }
    }

    async previewSlide(slide) {
      const video = slide.querySelector('.custom-video');

      if (!video) {
        await this.wait(this.slideDwell);
      } else {
        try {
          slide.classList.add('active-preview');
        
          video.muted = true;
          video.currentTime = 0;
        
          if (video.readyState < 1) {
            await new Promise((resolve) => {
              video.addEventListener('loadedmetadata', resolve, { once: true });
              video.preload = 'auto';
              video.load();
            });
          }
        
          // A video can limit its own preview length (seconds) via data-preview-max
          const previewMax = parseFloat(video.dataset.previewMax) || Infinity;
          const videoDuration = Math.min(video.duration || 5, previewMax);
        
          await video.play();
        
          const checkInterval = 1000;
          const totalChecks = Math.ceil((videoDuration * 1000) / checkInterval);
        
          for (let i = 0; i < totalChecks; i++) {
            if (!this.isPageVisible) {
              video.pause();
              break;
            }
            await this.wait(checkInterval);
          }
        
          video.pause();
          video.currentTime = 0;
        
          slide.classList.remove('active-preview');
        } catch (err) {
          console.warn('Video preview failed:', err);
          slide.classList.remove('active-preview');
          await this.wait(5000);
        }
      }
    }

    wait(ms) {
      return new Promise(resolve => {
        const timer = setTimeout(resolve, ms);
        this.previewTimers.push(timer);
      });
    }

    clearAllTimers() {
      this.previewTimers.forEach(timer => clearTimeout(timer));
      this.previewTimers = [];
    }

    pauseAllVideos() {
      const videos = this.querySelectorAll('.custom-video');
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
    
      const slides = this.querySelectorAll('.slide');
      slides.forEach(slide => {
        slide.classList.remove('active-preview');
      });
    }

    setupControls() {
      const prevBtn = this.querySelector(".embla__button--prev");
      const nextBtn = this.querySelector(".embla__button--next");

      if (prevBtn && nextBtn) {
        prevBtn.addEventListener("click", async () => {
          this.stopAutoplay();
          this.userInteracted = true;
          this.embla.scrollPrev();
        
          const newIndex = this.embla.selectedScrollSnap();
          this.preloadBatchMetadata(newIndex);
        
          await this.wait(600);
          this.userInteracted = false;
          this.startAutoplay();
        });
      
        nextBtn.addEventListener("click", async () => {
          this.stopAutoplay();
          this.userInteracted = true;
          this.embla.scrollNext();
        
          const newIndex = this.embla.selectedScrollSnap();
          this.preloadBatchMetadata(newIndex);
        
          await this.wait(600);
          this.userInteracted = false;
          this.startAutoplay();
        });

        this.embla.on("select", () => {
          prevBtn.disabled = !this.embla.canScrollPrev();
          nextBtn.disabled = !this.embla.canScrollNext();
        });

        prevBtn.disabled = !this.embla.canScrollPrev();
        nextBtn.disabled = !this.embla.canScrollNext();
      }
    }

    setupDots() {
      this.dotsContainer = this.querySelector(".dots-container");
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = "";

      this.embla.slideNodes().forEach((_, index) => {
        const dot = document.createElement("button");
        dot.className = "dot";
        dot.setAttribute("aria-label", `Go to review ${index + 1}`);
        dot.addEventListener("click", async () => {
          this.stopAutoplay();
          this.userInteracted = true;
          this.embla.scrollTo(index);
          this.preloadBatchMetadata(index);

          await this.wait(600);
          this.userInteracted = false;
          this.startAutoplay();
        });
        this.dotsContainer.appendChild(dot);
      });

      this.updateDots();
      this.embla.on("select", () => this.updateDots());
    }

    updateDots() {
      const selectedIndex = this.embla.selectedScrollSnap();
      this.dotsContainer.querySelectorAll(".dot").forEach((dot, index) => {
        dot.classList.toggle("active", index === selectedIndex);
      });
    }

    disconnectedCallback() {
      this.stopAutoplay();
    
      if (this.visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      }
    
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    
      if (this.embla) {
        this.embla.destroy();
        this.embla = null;
      }
    }
  }

  customElements.define("reviews-carousel", ReviewsCarousel);
}


if (!customElements.get('video-player-modal')) {
  class VideoPlayerModal extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isMuted = false;
      this.isPlaying = false;
      this.autoAdvance = true;
      this.previewCarousel = null;
      this.videoIndices = [];
    
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
      this.touchEndTime = 0;
    
      this.swipeDistanceThreshold = 75;
      this.swipeVelocityThreshold = 0.3;
      this.swipeAngleThreshold = 30;
      this.swipeMaxDuration = 500;
    
      this.isSwipeNavigating = false;
      this.swipeCooldown = 400;
    
      this.preloadedVideos = new Set();
    }

    connectedCallback() {
      this.closeBtn = this.querySelector('#close-player');
      this.muteToggle = this.querySelector('#mute-toggle');
      this.prevBtn = this.querySelector('#player-prev');
      this.nextBtn = this.querySelector('#player-next');
      this.videosContainer = this.querySelector('#player-videos-container');
      this.videoSlides = this.videosContainer.querySelectorAll('.player-slide');

      this.videoIndices = Array.from(this.videoSlides).map(
        slide => parseInt(slide.getAttribute('data-player-slide'))
      ).sort((a, b) => a - b);
    
      this.setupEventListeners();
      this.setupSwipeGestures();
      this.setupPreviewCarouselIntegration();
    }

    setupEventListeners() {
      this.closeBtn.addEventListener('click', () => this.closePlayer());
      this.muteToggle.addEventListener('click', () => this.toggleMute());

      this.prevBtn.addEventListener('click', () => {
        this.navigateVideo('prev');
      });

      this.nextBtn.addEventListener('click', () => {
        this.navigateVideo('next');
      });

      document.addEventListener('keydown', (e) => {
        if (!this.isPlaying) return;
      
        if (e.key === 'Escape') {
          this.closePlayer();
        } else if (e.key === 'ArrowLeft') {
          this.navigateVideo('prev');
        } else if (e.key === 'ArrowRight') {
          this.navigateVideo('next');
        } else if (e.key === ' ') {
          e.preventDefault();
          this.togglePlayPause();
        }
      });

      this.videoSlides.forEach((slide) => {
        const video = slide.querySelector('.player-video');
        const playButton = slide.querySelector('.play-button');

        if (video) {
          video.addEventListener('click', (e) => {
            e.stopPropagation();
            this.togglePlayPause();
          });

          if (playButton) {
            playButton.addEventListener('click', (e) => {
              e.stopPropagation();
              this.togglePlayPause();
            });
          }

          video.addEventListener('ended', () => {
            if (this.autoAdvance) {
              this.navigateVideo('next');
            }
          });
  
          video.addEventListener('timeupdate', () => {
            this.updateCountdown(video, slide);
          });
  
          video.addEventListener('loadedmetadata', () => {
            this.updateCountdown(video, slide);

            const slideIndex = parseInt(slide.getAttribute('data-player-slide'));
            this.preloadedVideos.add(slideIndex);
          });

          video.addEventListener('play', () => {
            if (playButton) playButton.classList.add('hidden');
          });

          video.addEventListener('pause', () => {
            if (playButton) playButton.classList.remove('hidden');
          });

          video.addEventListener('error', (e) => {
            console.error('Video error:', e);
            const slideIndex = parseInt(slide.getAttribute('data-player-slide'));
            this.preloadedVideos.delete(slideIndex);
          });
        }
      });
    }

    setupSwipeGestures() {
      this.videosContainer.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
        this.touchStartTime = Date.now();
      }, { passive: true });

      this.videosContainer.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        this.touchEndTime = Date.now();
        this.handleSwipeGesture();
      }, { passive: true });

      this.videosContainer.addEventListener('touchcancel', () => {
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchEndX = 0;
        this.touchEndY = 0;
      }, { passive: true });
    }

    handleSwipeGesture() {
      if (this.isSwipeNavigating) return;

      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;
      const duration = this.touchEndTime - this.touchStartTime;
    
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      const velocity = absDeltaX / duration;
    
      const angle = Math.abs(Math.atan2(deltaY, deltaX) * (180 / Math.PI));
      const isHorizontal = angle < this.swipeAngleThreshold || angle > (180 - this.swipeAngleThreshold);
    
      const isValidSwipe = 
        isHorizontal &&
        duration <= this.swipeMaxDuration &&
        (velocity >= this.swipeVelocityThreshold || absDeltaX >= this.swipeDistanceThreshold);
    
      if (isValidSwipe) {
        this.isSwipeNavigating = true;

        if (deltaX > 0) {
          this.navigateVideo('prev');
        } else {
          this.navigateVideo('next');
        }

        setTimeout(() => {
          this.isSwipeNavigating = false;
        }, this.swipeCooldown);
      }

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
    }

    preloadAdjacentVideos(currentIndex) {
      const currentPosition = this.videoIndices.indexOf(currentIndex);
    
      const prevPos = (currentPosition - 1 + this.videoIndices.length) % this.videoIndices.length;
      const prevIndex = this.videoIndices[prevPos];
      this.preloadVideoMetadata(prevIndex);
    
      const nextPos = (currentPosition + 1) % this.videoIndices.length;
      const nextIndex = this.videoIndices[nextPos];
      this.preloadVideoMetadata(nextIndex);
    }

    preloadVideoMetadata(videoIndex) {
      if (this.preloadedVideos.has(videoIndex)) return;

      const slide = Array.from(this.videoSlides).find(
        s => parseInt(s.getAttribute('data-player-slide')) === videoIndex
      );
    
      if (!slide) return;
    
      const video = slide.querySelector('.player-video');
      if (video && video.readyState < 1) {
        video.preload = 'metadata';
        video.load();
      }
    }

    async ensureVideoReady(video) {
      if (video.readyState >= 3) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Video load timeout'));
        }, 10000);

        video.addEventListener('canplay', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });

        video.addEventListener('error', (e) => {
          clearTimeout(timeout);
          reject(e);
        }, { once: true });

        if (video.readyState < 1) {
          video.preload = 'auto';
          video.load();
        }
      });
    }

    togglePlayPause() {
      const currentActiveSlide = this.querySelector('.player-slide.active-slide');
      if (!currentActiveSlide) return;

      const video = currentActiveSlide.querySelector('.player-video');
      if (!video) return;

      if (video.paused) {
        video.play().catch(err => console.warn('Play failed:', err));
      } else {
        video.pause();
      }
    }

    setupPreviewCarouselIntegration() {
      const carouselId = this.dataset.carousel;
      this.previewCarousel = carouselId
        ? document.getElementById(carouselId)
        : document.querySelector('reviews-carousel');
      if (!this.previewCarousel) return;

      const previewSlides = this.previewCarousel.querySelectorAll('[data-preview-slide]');
    
      previewSlides.forEach((slide) => {
        const video = slide.querySelector('.custom-video');
        if (!video) return;
      
        slide.addEventListener('click', () => {
          const slideVideoIndex = parseInt(slide.getAttribute('data-preview-slide'));
          this.openPlayer(slideVideoIndex);
        });
      });
    }

    openPlayer(startIndex = 0) {
      this.currentIndex = startIndex;
      this.autoAdvance = true;
    
      if (this.previewCarousel && this.previewCarousel.stopAutoplay) {
        this.previewCarousel.stopAutoplay();
      }

      this.classList.add('active-video-model');
      document.body.style.overflow = 'hidden';
    
      this.isPlaying = true;
    
      this.preloadAdjacentVideos(startIndex);
    
      this.showVideo(this.currentIndex);
    }

    closePlayer() {
      const currentActiveSlide = this.querySelector('.player-slide.active-slide');
      if (currentActiveSlide) {
        const video = currentActiveSlide.querySelector('.player-video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      }

      this.videoSlides.forEach(slide => {
        slide.classList.remove('active-slide');
      });

      this.classList.remove('active-video-model');
      document.body.style.overflow = '';
    
      this.isPlaying = false;
      this.isSwipeNavigating = false;
      this.preloadedVideos.clear();

      if (this.previewCarousel && this.previewCarousel.startAutoplay) {
        setTimeout(() => {
          this.previewCarousel.userInteracted = false;
          this.previewCarousel.startAutoplay();
        }, 500);
      }
    }

    async showVideo(videoIndex) {
      const targetSlide = Array.from(this.videoSlides).find(
        slide => parseInt(slide.getAttribute('data-player-slide')) === videoIndex
      );

      if (!targetSlide) {
        console.warn('No video found for index:', videoIndex);
        return;
      }

      const currentActiveSlide = this.querySelector('.player-slide.active-slide');
      if (currentActiveSlide) {
        const currentVideo = currentActiveSlide.querySelector('.player-video');
        if (currentVideo) {
          currentVideo.pause();
          currentVideo.currentTime = 0;
        }
      }

      this.currentIndex = videoIndex;

      this.videoSlides.forEach(slide => {
        slide.classList.remove('active-slide');
      });

      targetSlide.classList.add('active-slide');

      const video = targetSlide.querySelector('.player-video');
      const playButton = targetSlide.querySelector('.play-button');

      if (video) {
        video.muted = this.isMuted;
  
        try {
          if (playButton) playButton.classList.remove('hidden');
        
          await this.ensureVideoReady(video);
        
          video.currentTime = 0;
        
          if (playButton) playButton.classList.add('hidden');
        
          await video.play();
        
          this.preloadAdjacentVideos(videoIndex);
        } catch (err) {
          console.warn('Video play failed:', err);
        
          if (playButton) {
            playButton.classList.remove('hidden');
          }
        
          if (this.autoAdvance) {
            setTimeout(() => {
              this.navigateVideo('next');
            }, 1000);
          }
        }
      }
      else {
        this.preloadAdjacentVideos(videoIndex);
      }

      this.updateNavigationButtons();
    }

    navigateVideo(direction) {
      const currentPosition = this.videoIndices.indexOf(this.currentIndex);
    
      let nextPosition;
      if (direction === 'next') {
        nextPosition = (currentPosition + 1) % this.videoIndices.length;
      } else {
        nextPosition = (currentPosition - 1 + this.videoIndices.length) % this.videoIndices.length;
      }

      const nextIndex = this.videoIndices[nextPosition];
      this.showVideo(nextIndex);
    }

    updateNavigationButtons() {
      this.prevBtn.disabled = false;
      this.nextBtn.disabled = false;
    }

    toggleMute() {
      this.isMuted = !this.isMuted;
    
      this.videoSlides.forEach(slide => {
        const video = slide.querySelector('.player-video');
        if (video) {
          video.muted = this.isMuted;
        }
      });

      const muteIcon = this.muteToggle.querySelector('.mute-icon');
      const unmuteIcon = this.muteToggle.querySelector('.unmute-icon');
    
      if (this.isMuted) {
        muteIcon.classList.add('hidden');
        unmuteIcon.classList.remove('hidden');
      } else {
        muteIcon.classList.remove('hidden');
        unmuteIcon.classList.add('hidden');
      }
    }

    updateCountdown(video, slide) {
      const countdownEl = slide.querySelector('.video-countdown');
      if (!countdownEl || !video.duration) return;

      const remaining = Math.ceil(video.duration - video.currentTime);
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
    
      countdownEl.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }

  customElements.define('video-player-modal', VideoPlayerModal);
}
